import * as React from 'react';
import {
  ReactRouterLinkProvider,
  initializeLibraryI18n,
} from '@elevationscu/ui-component-lib';
import axios from 'axios';
import DOMPurify from 'dompurify';
import enTranslations from '@elevationscu/ui-component-lib/dist/locales/en/common.json';
import esTranslations from '@elevationscu/ui-component-lib/dist/locales/es/common.json';
import i18n from 'i18next';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import styles from './App.module.scss';
import prospectPut from './components/helper-functions';
import PersonalInfo from './components/personal-info';
import LoanType from './components/loan-type';
import LoanTimeline from './components/loan-timeline';
import UserCredit from './components/user-credit';
import PropertyType from './components/property-type';
import AdditionalQuestions from './components/additional-questions';

export const userContext = React.createContext();
// delete this comment
const options = {
  debug: process.env.NODE_ENV === 'development',

  // Language to use (overrides detected language)
  lng: 'en-US',
  // Fallback Language that will be used if translations in selected lng are not available
  fallbackLng: 'en-US',
  // Ignore the country code when looking up the locale file
  load: 'languageOnly',

  // Array of namespaces to load
  ns: [],
  // Default namespace used if not passed to translation function
  defaultNS: 'common',

  // Array of allowed, non-explicit languages w/o country codes
  whitelist: ['en', 'es'],
  nonExplicitWhitelist: true,

  // Whether to load locales asynchronously immediately after calling init routine
  // https://www.i18next.com/overview/configuration-options#initimmediate
  initImmediate: false,

  // Whether to save missing keys through the backend
  saveMissing: false,

  interpolation: {
    // not needed for react as it escapes by default
    escapeValue: false,
  },
};

// Initialize React integration
i18n.use(initReactI18next);

// init i18next
// for all options read: https://www.i18next.com/overview/configuration-options
if (!i18n.isInitialized) {
  i18n.init(options);
}

initializeLibraryI18n({
  resources: {
    en: {
      common: enTranslations,
    },
    es: {
      common: esTranslations,
    },
  },
  fallbackLng: 'en',
  lng: 'en-US',
  interpolation: {
    escapeValue: false,
  },
  defaultNS: 'common',
});

function App() {
  const data_form = 1;
  const loan_timeline = 2;
  const loan_type = 3;
  const user_credit = 4;
  const property_type = 5;
  const additional_questions = 6;
  const maxPages = 6;
  const [pageNumber, setPageNumber] = React.useState(1);

  const [userInfo, setUserInfo] = React.useState({
    application_id: null,
    first_name: null,
    last_name: null,
    email: null,
    phone: null,
    second_contact_first_name: null,
    second_contact_last_name: null,
    second_contact_email: null,
    second_contact_phone: null,
    loan_officer_id: null,
    application_data: {},
    app_status: null,
  });

  function nextPage(info) {
    setUserInfo(info);
    // page 1 (personal info) already calls this endpoint
    if (pageNumber !== 1) {
      prospectPut(info);
    }
    setPageNumber(pageNumber + 1);
  }

  function previousPage(_info) {
    setPageNumber(pageNumber - 1);
  }

  React.useEffect(() => {
    window.scrollTo(0, 0);
  });
  return (
    <main>

      <div className={styles.mainContainer}>

        <div className={styles.requestHeader}>
          <strong className={styles.requestHeaderSubText}>
            REQUEST A RATE PROPOSAL
          </strong>
        </div>
        <div className={styles.coreData}>
          {
            (() => {
              switch (pageNumber) {
                case data_form:
                  return (
                    <PersonalInfo
                      userInfo={userInfo}
                      setUserInfo={setUserInfo}
                      pageNumber={pageNumber}
                      nextPage={nextPage}
                      previousPage={previousPage}
                      maxPages={maxPages}
                    />
                  );
                case loan_timeline:
                  return (
                    <LoanTimeline
                      userInfo={userInfo}
                      setUserInfo={setUserInfo}
                      pageNumber={pageNumber}
                      nextPage={nextPage}
                      previousPage={previousPage}
                      maxPages={maxPages}
                    />
                  );
                case loan_type:
                  return (
                    <LoanType
                      userInfo={userInfo}
                      setUserInfo={setUserInfo}
                      pageNumber={pageNumber}
                      nextPage={nextPage}
                      previousPage={previousPage}
                      maxPages={maxPages}
                    />
                  );
                case user_credit:
                  return (
                    <UserCredit
                      userInfo={userInfo}
                      setUserInfo={setUserInfo}
                      pageNumber={pageNumber}
                      nextPage={nextPage}
                      previousPage={previousPage}
                      maxPages={maxPages}
                    />
                  );
                case property_type:
                  return (
                    <PropertyType
                      userInfo={userInfo}
                      setUserInfo={setUserInfo}
                      pageNumber={pageNumber}
                      nextPage={nextPage}
                      previousPage={previousPage}
                      maxPages={maxPages}
                    />
                  );
                case additional_questions:
                  return (
                    <AdditionalQuestions
                      userInfo={userInfo}
                      setUserInfo={setUserInfo}
                      pageNumber={pageNumber}
                      nextPage={nextPage}
                      previousPage={previousPage}
                      maxPages={maxPages}
                    />
                  );
                default:
                  console.error('Page does not exist');
              }
            })()
          }
        </div>
      </div>
    </main>
  );
}

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
    }],
  { basename: `${process.env.REACT_APP_PUBLIC_URL}` },
);

function useFirstRender() {
  const firstRender = React.useRef(true);

  React.useEffect(() => {
    firstRender.current = false;
  }, []);

  return firstRender.current;
}

function decodeHtmlEntities(html) {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

function injectHeadResources(resources) {
  if (!resources) return;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = decodeHtmlEntities(resources);

  Array.from(tempDiv.children).forEach((el) => {
    if (el.tagName === 'SCRIPT') {
      const script = document.createElement('script');
      if (el.src) script.src = el.src;
      script.text = el.textContent || '';
      script.async = false;
      document.head.appendChild(script);
    } else if (el.tagName === 'LINK' && el.getAttribute('rel') === 'stylesheet') {
      const clone = el.cloneNode(true);
      const href = clone.getAttribute('href');
      if (href && href.startsWith('/')) {
        clone.setAttribute('href', `https://elevationscu.com/${href}`);
      }
      document.head.appendChild(clone);
    } else {
      document.head.appendChild(el.cloneNode(true));
    }
  });
}

function injectBottomResources(resources) {
  if (!resources) return;
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = resources;

  Array.from(tempDiv.children).forEach((el) => {
    if (el.tagName === 'SCRIPT') {
      const script = document.createElement('script');
      if (el.src) script.src = el.src;
      script.text = el.textContent || '';
      script.async = false;
      document.body.appendChild(script);
    } else if (el.tagName === 'LINK' && el.getAttribute('rel') === 'stylesheet') {
      const clone = el.cloneNode(true);
      const href = clone.getAttribute('href');
      if (href && href.startsWith('/')) {
        clone.setAttribute('href', `https://elevationscu.com/${href}`);
      }
      document.body.appendChild(clone);
    } else {
      document.body.appendChild(el.cloneNode(true));
    }
  });
}

// function injectResources(resources, objLocation) {
//   if (resources) return;
//   const tempDiv = document.createElement('div');
//   tempDiv.innerHTML = decodeHtmlEntities(resources);

//   Array.from(tempDiv.children).forEach((el) => {
//     if (el.tagName === 'SCRIPT') {
//       const script = document.createElement('script');
//       if (el.src) script.src = el.src;
//       script.text = el.textContent || '';
//       script.async = false;
//       document.body.appendChild(script);
//     } else if (el.tagName === 'LINK' && el.getAttribute('rel') === 'stylesheet') {
//       const clone = el.cloneNode(true);
//       const href = clone.getAttribute('href');
//       if (href && href.startsWith('/')) {
//         clone.setAttribute('href', `https://elevationscu.com/${href}`);
//       }
//       if (objLocation === 'head') {
//         document.head.appendChild(clone);
//       } else {
//         document.body.appendChild(clone);
//       }
//     } else {
//       // eslint-disable-next-line
//       if (objLocation === 'head') {
//         document.head.appendChild(el.cloneNode(true));
//       } else {
//         document.body.appendChild(el.cloneNode(true));
//       }
//     }
//   });
// }

function AppWithRouterAccess() {
  const [headerHtml, setHeaderHtml] = React.useState('');
  const [footerHtml, setFooterHtml] = React.useState('');
  const [headResources, setHeadResources] = React.useState('');
  const [bottomResources, setBottomResources] = React.useState('');
  const isFirstRender = useFirstRender();
  const [sanitizedHeader, setSanitizedHeader] = React.useState('');
  const [sanitizedFooter, setSanitizedFooter] = React.useState('');

  React.useEffect(() => {
    const fetchHeaderFooter = async () => {
      if (isFirstRender) {
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/header`);
          setFooterHtml(res.data.footer);
          setHeaderHtml(res.data.header);
          setHeadResources(res.data.headResources);
          setBottomResources(res.data.bottomResources);
        } catch (err) {
          console.error('error', err);
        }
      }
    };
    fetchHeaderFooter();
  }, [isFirstRender]);

  React.useEffect(() => {
    if (headResources) {
      injectHeadResources(headResources);
    }
  }, [headResources]);

  React.useEffect(() => {
    if (bottomResources) {
      injectBottomResources(bottomResources);
    }
  }, [bottomResources]);

  React.useEffect(() => {
    if (headerHtml) {
      setSanitizedHeader(DOMPurify.sanitize(headerHtml));
    }
  }, [headerHtml]);

  React.useEffect(() => {
    if (footerHtml) {
      setSanitizedFooter(DOMPurify.sanitize(footerHtml));
    }
  }, [footerHtml]);

  return (

    <div>
      <I18nextProvider i18n={i18n}>
        <ReactRouterLinkProvider>
          {/* eslint-disable-next-line */}
          <div id="kentico-header" dangerouslySetInnerHTML={{ __html: sanitizedHeader }} />
          <RouterProvider
            router={router}
          />
          {/* eslint-disable-next-line */}
          <div id="kentico-footer" dangerouslySetInnerHTML={{ __html: sanitizedFooter }} />
        </ReactRouterLinkProvider>
      </I18nextProvider>
    </div>
  );
}

export default AppWithRouterAccess;
